import React, { useState, useRef } from 'react';
import { useApp } from '../context/AppContext';
import { Upload, FileText, Trash2, Download, Plus, X, File, Image, AlertTriangle } from 'lucide-react';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../firebase';
import { format, parseISO } from 'date-fns';

const DOCUMENT_CATEGORIES = [
  { value: 'w4', label: 'W-4' },
  { value: 'i9', label: 'I-9' },
  { value: 'id', label: 'Photo ID / License' },
  { value: 'certification', label: 'Certification' },
  { value: 'background_check', label: 'Background Check' },
  { value: 'contract', label: 'Contract / Agreement' },
  { value: 'writeup', label: 'Write-Up / Disciplinary' },
  { value: 'other', label: 'Other' }
];

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

function EmployeeDocuments({ employee }) {
  const { addEmployeeDocument, deleteEmployeeDocument, getEmployeeDocuments } = useApp();
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState('other');
  const [label, setLabel] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const documents = getEmployeeDocuments(employee.id);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      setError('File must be under 10 MB');
      return;
    }
    setError('');
    setSelectedFile(file);
    if (!label) {
      setLabel(file.name.replace(/\.[^/.]+$/, ''));
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      setError('Please select a file');
      return;
    }
    setUploading(true);
    setError('');

    try {
      const timestamp = Date.now();
      const ext = selectedFile.name.split('.').pop();
      const filename = `${category}_${timestamp}.${ext}`;
      const path = `employees/${employee.id}/documents/${filename}`;

      const storageRef = ref(storage, path);
      const snapshot = await uploadBytes(storageRef, selectedFile, {
        contentType: selectedFile.type,
        customMetadata: {
          employeeId: employee.id,
          category,
          originalName: selectedFile.name
        }
      });

      const downloadURL = await getDownloadURL(snapshot.ref);

      await addEmployeeDocument({
        employeeId: employee.id,
        category,
        label: label || selectedFile.name,
        fileName: selectedFile.name,
        fileType: selectedFile.type,
        fileSize: selectedFile.size,
        storagePath: path,
        url: downloadURL
      });

      // Reset form
      setSelectedFile(null);
      setLabel('');
      setCategory('other');
      setShowUpload(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      console.error('Document upload error:', err);
      setError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.label}"? This cannot be undone.`)) return;

    try {
      // Delete from storage
      if (doc.storagePath) {
        const storageRef = ref(storage, doc.storagePath);
        await deleteObject(storageRef).catch(() => {});
      }
      await deleteEmployeeDocument(doc.id);
    } catch (err) {
      console.error('Delete error:', err);
      alert('Failed to delete document.');
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType && fileType.startsWith('image/')) return <Image size={18} />;
    return <FileText size={18} />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getCategoryLabel = (value) => {
    return DOCUMENT_CATEGORIES.find(c => c.value === value)?.label || value;
  };

  return (
    <div>
      <div className="timeoff-header">
        <h3><FileText size={18} /> Documents</h3>
        <button
          className={`btn ${showUpload ? 'btn-secondary' : 'btn-primary'} btn-sm`}
          onClick={() => { setShowUpload(!showUpload); setError(''); }}
        >
          {showUpload ? <><X size={14} /> Cancel</> : <><Plus size={14} /> Upload Document</>}
        </button>
      </div>

      {showUpload && (
        <form onSubmit={handleUpload} className="manager-timeoff-form">
          <div className="mtf-row">
            <div className="mtf-group">
              <label>Category</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)}>
                {DOCUMENT_CATEGORIES.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div className="mtf-group mtf-grow">
              <label>Label</label>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. 2026 W-4 Form"
              />
            </div>
          </div>
          <div className="mtf-row">
            <div className="mtf-group mtf-grow">
              <label>File *</label>
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileSelect}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.txt,.csv,.xls,.xlsx"
                style={{ fontSize: '13px' }}
              />
              <span className="mtf-hint">PDF, Word, images, spreadsheets up to 10 MB</span>
            </div>
          </div>
          {error && (
            <div className="emp-doc-error">
              <AlertTriangle size={14} /> {error}
            </div>
          )}
          <div className="mtf-footer">
            <div className="mtf-left">
              {selectedFile && (
                <span className="mtf-day-count">
                  <File size={13} /> {selectedFile.name} ({formatFileSize(selectedFile.size)})
                </span>
              )}
            </div>
            <button type="submit" className="btn btn-primary btn-sm" disabled={uploading}>
              {uploading ? 'Uploading...' : <><Upload size={14} /> Upload</>}
            </button>
          </div>
        </form>
      )}

      {documents.length === 0 && !showUpload ? (
        <div className="empty-timeoff">No documents uploaded</div>
      ) : (
        <div className="emp-doc-list">
          {documents.map(doc => (
            <div key={doc.id} className="emp-doc-row">
              <div className="emp-doc-row-left">
                <span className="emp-doc-icon">
                  {getFileIcon(doc.fileType)}
                </span>
                <div className="emp-doc-info">
                  <a
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="emp-doc-name"
                  >
                    {doc.label}
                  </a>
                  <span className="emp-doc-meta">
                    {getCategoryLabel(doc.category)}
                    {doc.fileSize && <> &middot; {formatFileSize(doc.fileSize)}</>}
                    {doc.uploadedAt && <> &middot; {format(parseISO(doc.uploadedAt), 'MMM d, yyyy')}</>}
                  </span>
                </div>
              </div>
              <div className="emp-doc-actions">
                <a
                  href={doc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="emp-doc-action-btn"
                  title="Download"
                >
                  <Download size={15} />
                </a>
                <button
                  className="emp-doc-action-btn emp-doc-delete"
                  onClick={() => handleDelete(doc)}
                  title="Delete"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default EmployeeDocuments;
